<script setup lang="ts">
import { cartBus } from '@/event/cartBus'
import {
    clearMessageAlertElementHappy,
    messageAlertElementBasketHappy
} from '@/happyTheme/event/modalBasket/messageAlertElementBasketHappy'
import { Trash2, X, Check } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

function toggleShowAlertMessageElementHappy() {
    clearMessageAlertElementHappy()
}

function deleteElementBasketHappy() {
    const productIndex = cartBus.value.findIndex(
        (p) => p.product_id === messageAlertElementBasketHappy.value.product.product_id
    )
    cartBus.value.splice(productIndex, 1)
    clearMessageAlertElementHappy()
}
</script>

<template>
    <section
        v-if="messageAlertElementBasketHappy.message.length !== 0"
        class="section-alert-element-basket-happy"
    >
        <section
            class="alert-element-overlay"
            @click="toggleShowAlertMessageElementHappy()"
        ></section>

        <transition
            name="alert-element"
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <section
                class="alert-element-card"
                role="alertdialog"
                aria-modal="true"
                :aria-label="t('removeProductQuestion')"
            >
                <!-- Üst danger şeridi -->
                <span class="alert-element-topbar" aria-hidden="true"></span>

                <!-- Kapat -->
                <button
                    type="button"
                    class="alert-element-close"
                    :aria-label="t('close')"
                    @click="toggleShowAlertMessageElementHappy()"
                >
                    <X class="w-4 h-4" :stroke-width="2.5" />
                </button>

                <!-- İkon chip -->
                <div class="alert-element-icon-chip" aria-hidden="true">
                    <Trash2 class="w-6 h-6" :stroke-width="2" />
                </div>

                <!-- Başlık -->
                <p class="alert-element-title">
                    {{ t('removeProductQuestion') }}
                </p>

                <!-- Ürün adı rozeti -->
                <p class="alert-element-product" v-if="messageAlertElementBasketHappy.product_name">
                    {{ messageAlertElementBasketHappy.product_name }}
                </p>

                <!-- Butonlar -->
                <div class="alert-element-actions">
                    <button
                        type="button"
                        class="alert-element-btn-danger"
                        @click="deleteElementBasketHappy()"
                    >
                        <Check class="w-4 h-4" :stroke-width="2.25" />
                        <span>{{ t('delete') }}</span>
                    </button>

                    <button
                        type="button"
                        class="alert-element-btn-secondary"
                        @click="toggleShowAlertMessageElementHappy()"
                    >
                        <X class="w-4 h-4" :stroke-width="2.25" />
                        <span>{{ t('cancel') }}</span>
                    </button>
                </div>
            </section>
        </transition>
    </section>
</template>

<style scoped>
.section-alert-element-basket-happy {
    @apply absolute inset-0 z-30 h-full w-full flex items-center justify-center;
}

.alert-element-overlay {
    @apply absolute inset-0;
    @apply bg-text-dark-happy-light/50;
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
}

.alert-element-card {
    @apply relative w-[20rem] max-w-[calc(100%-1.5rem)] z-30 flex flex-col items-center
           rounded-2xl px-5 pt-8 pb-5 gap-3 overflow-hidden;
    @apply bg-t-card text-t-text ring-1 ring-t-border shadow-2xl;
    @apply happy-ifo-dark:ring-t-danger/30 happy-ifo-dark:shadow-black/40;
}

.alert-element-topbar {
    @apply absolute top-0 left-0 right-0 h-1;
    background: linear-gradient(
        90deg,
        rgb(var(--t-danger) / 0.55),
        rgb(var(--t-danger)),
        rgb(var(--t-danger) / 0.55)
    );
}

.alert-element-close {
    @apply absolute top-2.5 right-2.5 w-7 h-7 rounded-full
           flex items-center justify-center cursor-pointer
           text-t-text-muted transition-colors duration-150;
}
.alert-element-close:hover,
.alert-element-close:focus {
    @apply text-t-text bg-t-border/40 outline-none;
}

.alert-element-icon-chip {
    @apply w-14 h-14 rounded-full flex items-center justify-center;
    background-color: rgb(var(--t-danger) / 0.15);
    color: rgb(var(--t-danger));
    box-shadow: inset 0 0 0 1px rgb(var(--t-danger) / 0.3);
}

.alert-element-title {
    @apply text-center font-semibold text-[0.95rem] leading-snug px-2;
}

.alert-element-product {
    @apply text-center text-sm font-medium rounded-md px-3 py-1.5 max-w-full break-words;
    background-color: rgb(var(--t-danger) / 0.1);
    color: rgb(var(--t-danger));
}

.alert-element-actions {
    @apply flex flex-col w-full gap-2 mt-1;
}

/* Primary - danger (sil) */
.alert-element-btn-danger {
    @apply flex items-center justify-center gap-2
           font-semibold rounded-lg py-2.5 px-4 w-full
           shadow-md text-white
           transition-all duration-150 cursor-pointer;
    background-color: rgb(var(--t-danger));
}
.alert-element-btn-danger:hover,
.alert-element-btn-danger:focus {
    background-color: rgb(var(--t-danger) / 0.88);
    @apply shadow-lg outline-none;
    transform: translateY(-1px);
}
.alert-element-btn-danger:active {
    background-color: rgb(var(--t-danger) / 0.95);
    @apply shadow-sm;
    transform: translateY(0);
}

/* Secondary - outline (iptal) */
.alert-element-btn-secondary {
    @apply flex items-center justify-center gap-2
           font-medium rounded-lg py-2.5 px-4 w-full
           bg-transparent text-t-text ring-1 ring-t-border
           transition-all duration-150 cursor-pointer;
}
.alert-element-btn-secondary:hover,
.alert-element-btn-secondary:focus {
    @apply bg-t-border/25 ring-t-border-strong outline-none;
}
.alert-element-btn-secondary:active {
    @apply bg-t-border/40;
}
</style>
